import React from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Box, Flex, Heading, Text, Spinner, Button } from '@chakra-ui/react';
import Layout from './components/Layout';
import DocPage from './components/DocPage';
import LoginPage from './auth/LoginPage';
import { useAuth } from './auth/AuthContext';
import { useDocs } from './docs/DocsContext';

function NotFound() {
  return (
    <Box maxW="740px" py={10}>
      <Heading as="h1" fontSize="28px" letterSpacing="-0.03em" mb={3}>문서를 찾을 수 없습니다</Heading>
      <Text color="neutral.600">주소가 바뀌었거나 삭제된 문서입니다. 왼쪽 목록에서 찾아 주세요.</Text>
    </Box>
  );
}

// 원장을 못 받았을 때 (피드 모드에서 docs-feed 가 응답하지 않는 경우). 빈 목록을 "문서가 없다" 로
// 보여 주면 사용자도 우리도 원인을 모른다 — 실패는 실패로 보인다.
function DocsUnavailable({ onRetry }) {
  return (
    <Box maxW="740px" py={10}>
      <Heading as="h1" fontSize="24px" letterSpacing="-0.03em" mb={3}>문서 목록을 불러오지 못했습니다</Heading>
      <Text color="neutral.600" mb={5}>잠시 후 다시 시도해 주세요. 계속 안 되면 레이블리에 알려 주세요.</Text>
      <Button size="sm" onClick={onRetry} bg="brand.500" color="white" _hover={{ bg: 'brand.600' }}>다시 시도</Button>
    </Box>
  );
}

function Home() {
  const { visiblePages, pages } = useDocs();
  const first = visiblePages[0] || pages[0];
  if (!first) return <NotFound />;
  return <Navigate to={`/${first.slug}`} replace />;
}

// 동적 라우트 — 원장(bySlug)에 있는 slug 만 문서로 그린다. 없으면 404.
function DocRoute() {
  const { slug } = useParams();
  const { bySlug } = useDocs();
  const page = bySlug[slug];
  if (!page) return <NotFound />;
  return <DocPage page={page} />;
}

export default function App() {
  const { status, signedIn } = useAuth();
  const docs = useDocs();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <Flex minH="100dvh" align="center" justify="center" bg="neutral.50">
        <Spinner color="brand.500" />
      </Flex>
    );
  }

  // 로그인 화면은 사이드바 없이 단독으로 띄운다. 이미 로그인했다면 돌아갈 곳으로 보낸다.
  if (location.pathname === '/login') {
    if (signedIn) {
      const next = new URLSearchParams(location.search).get('next');
      return <Navigate to={next && next.startsWith('/') ? next : '/'} replace />;
    }
    return <LoginPage />;
  }

  // 로그인하지 않아도 레이아웃은 그대로 띄운다. 공개 문서만 목록에 나오고,
  // 나머지 주소로 직접 들어오면 DocPage 가 로그인 안내를 보여준다.
  let body;
  if (docs.status === 'loading') {
    body = <Flex py={16} justify="center"><Spinner color="brand.500" /></Flex>;
  } else if (docs.status === 'error') {
    body = <DocsUnavailable onRetry={docs.reload} />;
  } else {
    body = (
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/:slug" element={<DocRoute />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    );
  }
  return <Layout>{body}</Layout>;
}
